import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OrderSummaryModal from '../components/OrderSummaryModal';
import OrderInfoModal from '../components/OrderInfoModal';
import PendingStockInfoModal from '../components/PendingStockInfoModal';
import Loader from '../components/Loader';
import type { OrderCustomization } from '../types/database';
import { productService, type ProductForGroup, type OrderChangeResult, type GroupCustomization, type CatalogGroupInfo } from '../services/product.service';
import {
  type DeliveryChoice,
  type DeliveryOption,
  type GroupDeliveryAddress,
  pickGroupDeliveryAddress,
} from '../components/DeliveryAddressForm';
import {
  resolveStockStatus,
  isStockStatusBuyable,
  type StockStatus,
} from '../lib/stockStatus';
import { InfoIcon } from 'lucide-react';

/**
 * LocalStorage key for user data
 */
const USER_DATA_KEY = 'fraternityUserData';

/**
 * Catalog-derived group data (no Supabase). Same shape as the old GroupWithRelations
 * so the UI can use it without changes.
 */
interface CatalogGroupData {
  id: string;
  name: string;
  code: string;
  group_garments: { garment_id: number; garment: { id: number; name: string } }[];
  group_colors: {
    garment_id: number;
    color_id: number;
    color: { id: number; name: string; hex_code: string };
    images?: { front_image_url?: string; back_image_url?: string; side_image_url?: string; detail_image_url?: string };
  }[];
  group_sizes: { size_id: number; size: { id: number; name: string } }[];
  group_customizations: GroupCustomization[];
  delivery_option: DeliveryOption;
  individual_option_price: number | null;
  grouped_option_price: number | null;
  delivery_addresses: GroupDeliveryAddress[];
}

function parseDeliveryOption(value: unknown): DeliveryOption {
  if (value === 'hybrid' || value === 'grouped' || value === 'individual') return value;
  return 'individual';
}

function parseOptionalPrice(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Resolve the user's delivery choice from group mode + stored shipping_type. */
function resolveDeliveryChoice(
  groupOption: DeliveryOption,
  userShippingType?: unknown
): DeliveryChoice {
  if (groupOption === 'grouped') return 'grouped';
  if (groupOption === 'individual') return 'individual';
  // hybrid: always keep the user's previous choice on change/re-login
  if (userShippingType === 'grouped' || userShippingType === 'individual') {
    return userShippingType;
  }
  // Fresh hybrid order: preselect individual in the summary modal
  return 'individual';
}

/** Sync read so change-orders don't briefly price as individual before userData loads. */
function readStoredDeliveryChoice(): DeliveryChoice {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return 'individual';
    const parsed = JSON.parse(raw);
    return resolveDeliveryChoice(
      parseDeliveryOption(parsed?.group?.delivery_option),
      parsed?.user?.shipping_type
    );
  } catch {
    return 'individual';
  }
}

function buildCatalogGroupData(
  groupInfo: CatalogGroupInfo,
  products: ProductForGroup[],
  customizations: GroupCustomization[],
  storedGroup?: Record<string, unknown> | null
): CatalogGroupData {
  const garmentsMap = new Map<number, { garment_id: number; garment: { id: number; name: string } }>();
  const colorsMap = new Map<string, CatalogGroupData['group_colors'][0]>();
  const sizesMap = new Map<number, { size_id: number; size: { id: number; name: string } }>();

  products.forEach(p => {
    if (!garmentsMap.has(p.garment_id)) {
      garmentsMap.set(p.garment_id, { garment_id: p.garment_id, garment: { id: p.garment_id, name: p.garment_name } });
    }
    const colorKey = `${p.garment_id}_${p.color_id}`;
    if (!colorsMap.has(colorKey)) {
      colorsMap.set(colorKey, {
        garment_id: p.garment_id,
        color_id: p.color_id,
        color: { id: p.color_id, name: p.color_name, hex_code: p.color_hex || '#000000' },
        images: {
          front_image_url: p.front_image_url ?? undefined,
          back_image_url: p.back_image_url ?? undefined,
          side_image_url: p.side_image_url ?? undefined,
          detail_image_url: p.detail_image_url ?? undefined,
        },
      });
    }
    if (!sizesMap.has(p.size_id)) {
      sizesMap.set(p.size_id, { size_id: p.size_id, size: { id: p.size_id, name: p.size_name } });
    }
  });

  return {
    id: groupInfo.id,
    name: groupInfo.name,
    code: groupInfo.code,
    group_garments: [...garmentsMap.values()],
    group_colors: [...colorsMap.values()],
    group_sizes: [...sizesMap.values()],
    group_customizations: customizations,
    delivery_option: parseDeliveryOption(groupInfo.delivery_option ?? storedGroup?.delivery_option),
    individual_option_price: parseOptionalPrice(
      groupInfo.individual_option_price ?? storedGroup?.individual_option_price
    ),
    grouped_option_price: parseOptionalPrice(
      groupInfo.grouped_option_price ?? storedGroup?.grouped_option_price
    ),
    delivery_addresses: (groupInfo.delivery_addresses
      ?? storedGroup?.delivery_addresses
      ?? []) as GroupDeliveryAddress[],
  };
}

/**
 * Temporary customization type for order creation
 */
export type TempCustomization = Omit<OrderCustomization, 'order_id' | 'created_at' | 'updated_at'> & {
  option: {
    name: string;
  };
  price?: number;
  custom_value?: string;
  is_locked?: boolean;
};

/**
 * Order state interface with additional fields for presentation
 */
interface OrderState {
  id?: string;
  group_id: string;
  name: string;
  email: string;
  phone: string;
  garment_id?: number;
  color_id?: number;
  size_id?: number;
  product_id?: number;
  garment_name?: string;
  color_name?: string;
  color_hex_code?: string;
  size_name?: string;
  garment_price?: number;
  color_price?: number;
  size_price?: number;
  customizations?: TempCustomization[];
  status?: string;
  total_price: number;
  is_change?: boolean;
  order_change?: OrderChangeResult | null;
  delivery_option?: DeliveryOption;
  delivery_choice?: DeliveryChoice;
  delivery_price?: number;
  final_price?: number;
  group_delivery_address?: GroupDeliveryAddress;
}

export default function CustomizeOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupCode = searchParams.get('code');

  // ====================
  // STATE MANAGEMENT
  // ====================
  
  // Basic states
  const [isLoading, setIsLoading] = useState(true);
  const [groupData, setGroupData] = useState<CatalogGroupData | null>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [showOrderInfoModal, setShowOrderInfoModal] = useState(false);

  // Order change states
  const [isChange, setIsChange] = useState<boolean>(false);
  const [orderChange, setOrderChange] = useState<OrderChangeResult | null>(null);

  // Product selection states
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<{id: number; name: string; hex_code: string} | null>(null);
  const [selectedGarment, setSelectedGarment] = useState<{id: number; name: string} | null>(null);
  const [customOptions, setCustomOptions] = useState<{[key: number]: string}>({});
  const [deliveryChoice, setDeliveryChoice] = useState<DeliveryChoice>(readStoredDeliveryChoice);

  // Price calculation states
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [priceBreakdown, setPriceBreakdown] = useState<{
    garment: number;
    color: number;
    size: number;
    customizations: number;
    delivery_price: number;
  }>({
    garment: 0,
    color: 0,
    size: 0,
    customizations: 0,
    delivery_price: 0
  });

  // Backend error / loading states
  const [catalogError, setCatalogError] = useState(false);
  const [priceError, setPriceError] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);

  const isOrderChanged = useMemo(() => {
    if (!orderData) return false;

    // 1️⃣ Garment changed
    const garmentChanged =
      selectedGarment?.id !== orderData.garmentId;
    
    // 2️⃣ Color changed
    const colorChanged =
      selectedColor?.id !== orderData.colorId;

    // 3️⃣ Size changed
    const sizeChanged =
      selectedSize !== orderData.sizeId;
    
    // 4️⃣ Custom options changed
    // orderData.customOptions can be either an array (from user data) or an object (after confirmation)
    const prevOptions = orderData.customOptions || [];
    
    // Normalize prevOptions to an object format for comparison
    let prevOptionsObject: {[key: number]: string} = {};
    if (Array.isArray(prevOptions)) {
      // Convert array format to object format, filtering out empty values
      prevOptions.forEach((opt: any) => {
        if (opt.option_id && opt.value && opt.value !== 'false' && opt.value !== '') {
          prevOptionsObject[opt.option_id] = opt.value;
        }
      });
    } else {
      // Already an object, just filter out empty values
      Object.entries(prevOptions).forEach(([key, value]) => {
        if (value && value !== 'false' && value !== '') {
          prevOptionsObject[Number(key)] = String(value);
        }
      });
    }
    
    // Current customOptions, filtering out empty values
    const currentOptionsObject: {[key: number]: string} = {};
    Object.entries(customOptions).forEach(([key, value]) => {
      if (value && value !== 'false' && value !== '') {
        currentOptionsObject[Number(key)] = value;
      }
    });
    
    // Compare the two objects
    const prevKeys = Object.keys(prevOptionsObject).sort();
    const currentKeys = Object.keys(currentOptionsObject).sort();
    
    const customOptionsChanged = 
      prevKeys.length !== currentKeys.length ||
      prevKeys.some((key, index) => key !== currentKeys[index]) ||
      prevKeys.some(key => prevOptionsObject[Number(key)] !== currentOptionsObject[Number(key)]);

    // 5️⃣ Delivery choice changed (hybrid groups only)
    const previousShipping = orderData.shipping_type;
    const deliveryChanged =
      groupData?.delivery_option === 'hybrid' &&
      (previousShipping === 'grouped' || previousShipping === 'individual') &&
      deliveryChoice !== previousShipping;

    return (
      garmentChanged ||
      colorChanged ||
      sizeChanged ||
      customOptionsChanged ||
      deliveryChanged
    );
  }, [orderData, selectedGarment, selectedColor, selectedSize, customOptions, deliveryChoice, groupData?.delivery_option]);

  // Image viewer states
  const [currentImageView, setCurrentImageView] = useState<'front' | 'back' | 'side' | 'detail'>('front');
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<1.5 | 2 | 2.5>(2);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  // Stock availability & catalog data
  // STOCK CHECKING: productAvailability maps "garmentId_colorId_sizeId" → StockStatus (from resolveStockStatus)
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [productAvailability, setProductAvailability] = useState<{
    [key: string]: StockStatus;
  }>({});
  const [catalogProducts, setCatalogProducts] = useState<ProductForGroup[]>([]);

  // Summary modal states
  const [showSummary, setShowSummary] = useState(false);
  const [order, setOrder] = useState<OrderState | null>(null);
  const [showPendingStockInfo, setShowPendingStockInfo] = useState(false);

  // ====================
  // PRICE MAPS FROM CATALOG
  // ====================

  const garmentPriceMap = useMemo(() => {
    const map: {[id: number]: number} = {};
    catalogProducts.forEach(p => { if (!(p.garment_id in map)) map[p.garment_id] = Number(p.garment_base_price) || 0; });
    return map;
  }, [catalogProducts]);

  const colorPriceMap = useMemo(() => {
    const map: {[id: number]: number} = {};
    catalogProducts.forEach(p => { if (!(p.color_id in map)) map[p.color_id] = Number(p.color_base_price) || 0; });
    return map;
  }, [catalogProducts]);

  const sizePriceMap = useMemo(() => {
    const map: {[id: number]: number} = {};
    catalogProducts.forEach(p => { if (!(p.size_id in map)) map[p.size_id] = Number(p.size_base_price) || 0; });
    return map;
  }, [catalogProducts]);

  // ====================
  // UTILITY FUNCTIONS
  // ====================

  /**
   * Generates a unique key for product availability map
   */
  const getAvailabilityKey = useCallback((garmentId: number, colorId: number, sizeId: number): string => {
    return `${garmentId}_${colorId}_${sizeId}`;
  }, []);

  /**
   * Checks if a specific product combination is available in stock
   * STOCK CHECKING (core): looks up productAvailability[key] (StockStatus from resolveStockStatus).
   * Can buy when status is in_stock or pending_incoming.
   */
  const isProductAvailableInStock = useCallback((garmentId: number, colorId: number, sizeId: number): boolean => {
    if (isLoadingAvailability) return true;

    // The customer already owns this exact garment/color/size combination —
    // never block it on stock. Otherwise a combination that later sells out
    // would prevent them from saving unrelated changes (personalizations,
    // delivery method, etc.) to a product they already purchased.
    if (isChange && orderData) {
      const sizeName = groupData?.group_sizes.find((s: any) => s.size.id === sizeId)?.size.name;
      if (
        garmentId === orderData.garmentId &&
        colorId === orderData.colorId &&
        sizeName === orderData.sizeId
      ) {
        return true;
      }
    }

    // STOCK CHECKING: actual stock lookup — key → StockStatus
    const key = getAvailabilityKey(garmentId, colorId, sizeId);
    return isStockStatusBuyable(productAvailability[key]);
  }, [productAvailability, getAvailabilityKey, isLoadingAvailability, isChange, orderData, groupData]);

  /**
   * STOCK CHECKING: true when the combo is sellable only because incoming reserved stock
   * (pending_incoming). Used to show the longer-dispatch message.
   */
  const isPendingIncomingStock = useCallback((garmentId: number, colorId: number, sizeId: number): boolean => {
    if (isLoadingAvailability) return false;
    const key = getAvailabilityKey(garmentId, colorId, sizeId);
    return productAvailability[key] === 'pending_incoming';
  }, [productAvailability, getAvailabilityKey, isLoadingAvailability]);

  /**
   * Checks if a color is buyable for the selected garment
   * If size is selected, checks specific combination
   * Otherwise, checks if color has at least one available size
   * STOCK CHECKING (UI): used to disable out-of-stock color buttons
   */
  const isColorAvailable = useCallback((colorId: number): boolean => {
    if (isLoadingAvailability || !selectedGarment) return true;

    // Check specific combination if size is selected
    if (selectedSize) {
      const sizeData = groupData?.group_sizes.find((s: any) => s.size.name === selectedSize);
      if (sizeData) {
        return isProductAvailableInStock(selectedGarment.id, colorId, sizeData.size.id);
      }
    }

    // Check if color has at least one available size
    if (groupData) {
      for (const sizeOption of groupData.group_sizes) {
        if (isProductAvailableInStock(selectedGarment.id, colorId, sizeOption.size.id)) {
          return true;
        }
      }
      return false;
    }

    return true;
  }, [selectedGarment, selectedSize, groupData, isProductAvailableInStock, isLoadingAvailability]);

  /**
   * Checks if a size is available for the selected garment and color
   * STOCK CHECKING (UI): used to disable out-of-stock size buttons
   */
  const isSizeAvailable = useCallback((sizeId: number): boolean => {
    if (!selectedGarment || !selectedColor) return true;

    return isProductAvailableInStock(selectedGarment.id, selectedColor.id, sizeId);
  }, [selectedGarment, selectedColor, isProductAvailableInStock]);

  // ====================
  // EFFECTS
  // ====================

  /**
   * Reset zoom position when zoom is activated
   */
  useEffect(() => {
    if (isZoomed) {
      setZoomPosition({ x: 50, y: 50 });
    }
  }, [isZoomed]);

  /**
   * Reset image view when product or color changes
   */
  useEffect(() => {
    setCurrentImageView('front');
    if (isZoomed) {
      setIsZoomed(false);
    }
  }, [selectedColor?.id, selectedGarment?.id]);

  /**
   * Handle keyboard navigation for image viewer
   */
  useEffect(() => {
    // Solo ejecutar si hay datos necesarios
    if (!selectedGarment || !selectedColor || !groupData) return;
    
    const colorData = groupData.group_colors.find((c: any) => 
      c.color_id === selectedColor.id && 
      c.garment_id === selectedGarment.id
    );
    
    if (!colorData?.images) return;
    
    // Verificar si hay imágenes disponibles
    const hasImages = 
      colorData.images.front_image_url || 
      colorData.images.back_image_url || 
      colorData.images.side_image_url || 
      colorData.images.detail_image_url;
      
    if (!hasImages) return;
    
    const availableImages = [
      { key: 'front', url: colorData.images.front_image_url },
      { key: 'back', url: colorData.images.back_image_url },
      { key: 'side', url: colorData.images.side_image_url },
      { key: 'detail', url: colorData.images.detail_image_url }
    ].filter(img => img.url);
    
    if (availableImages.length <= 1) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevenir la navegación predeterminada con las teclas de flecha
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        if (!isZoomed || ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault();
        }
      }
      
      const currentIndex = availableImages.findIndex(img => img.key === currentImageView);
      
      // No navegar con flechas izquierda/derecha durante zoom, pero permitir arriba/abajo para cambiar nivel de zoom
      if (!isZoomed) {
        // Navegar con teclas de flecha
        if (e.key === 'ArrowLeft') {
          const prevIndex = (currentIndex - 1 + availableImages.length) % availableImages.length;
          setCurrentImageView(availableImages[prevIndex].key as any);
        } else if (e.key === 'ArrowRight') {
          const nextIndex = (currentIndex + 1) % availableImages.length;
          setCurrentImageView(availableImages[nextIndex].key as any);
        }
      } else {
        // Si está en zoom, usar arriba/abajo para cambiar el nivel de zoom
        if (e.key === 'ArrowUp') {
          setZoomLevel(prev => prev < 2.5 ? (prev + 0.5) as 1.5 | 2 | 2.5 : prev);
        } else if (e.key === 'ArrowDown') {
          setZoomLevel(prev => prev > 1.5 ? (prev - 0.5) as 1.5 | 2 | 2.5 : prev);
        }
      }
      
      // Quitar zoom con ESC
      if (e.key === 'Escape' && isZoomed) {
        setIsZoomed(false);
      }
    };
    
    // Verificar que la vista actual es válida
    const currentViewIsValid = availableImages.some(img => img.key === currentImageView);
    if (!currentViewIsValid && availableImages.length > 0) {
      // Si la vista actual no es válida, establecer la primera vista disponible
      setCurrentImageView(availableImages[0].key as any);
    }
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageView, isZoomed, selectedColor, selectedGarment, groupData, zoomLevel]);

  /**
   * Selects the first color for a given garment (no stock-based preselection —
   * user must be able to pick any catalog color/size, including out-of-stock).
   */
  const selectFirstColor = (garmentId: number, group: CatalogGroupData) => {
    const colors = group.group_colors.filter((c: any) => c.garment_id === garmentId);
    if (colors.length === 0) {
      setSelectedColor(null);
      return;
    }

    const firstColor = colors[0];
    setSelectedColor({
      id: firstColor.color.id,
      name: firstColor.color.name,
      hex_code: firstColor.color.hex_code,
    });
  };

  /**
   * Safely extracts and verifies product data from user data
   */
  const safelyExtractProductData = (userData: any, groupData: any) => {
    try {
      if (!userData || !groupData) return false;
      
      // Verificar si hay producto asignado
      const hasProductId = !!userData.product_id;
      const hasProductObject = userData.product && typeof userData.product === 'object';
      
      // Si no hay producto, no hay nada que extraer
      if (!hasProductId && !hasProductObject) {
        return false;
      }
      
      // Primero intentamos extraer de userData.product si existe
      if (hasProductObject) {
        const productData = userData.product;
        
        // Verificar si tiene la información mínima requerida
        const hasGarment = productData.garment_id && productData.garment;
        const hasColor = productData.color_id && productData.color;
        const hasSize = productData.size_id && productData.size;
        
        if (hasGarment && hasColor && hasSize) {
          // Establecer prenda
          const garment = {
            id: productData.garment_id,
            name: productData.garment?.name || 'Prenda'
          };
          setSelectedGarment(garment);
          
          setSelectedColor({
            id: productData.color_id,
            name: productData.color?.name || 'Color',
            hex_code: productData.color?.hex_code || '#000000',
          });
          
          // Establecer talla
          const sizeName = productData.size?.name || '';
          if (sizeName) {
            setSelectedSize(sizeName);
          }
          
          return true;
        }
      }
      
      // Si tenemos product_id pero no pudimos extraer de userData.product,
      // intentamos buscar el producto en los datos del grupo
      if (hasProductId) {
        // Este enfoque sería más complejo ya que requeriría buscar el producto en la base de datos

        // En este punto, lo mejor es esperar a que la carga completa se realice en otro lugar
        return false;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        // Limpiar cualquier dato guardado previamente para asegurar que siempre pida la información
        localStorage.removeItem(USER_DATA_KEY);
        
        if (!groupCode) {
          toast.error('Se requiere un código de grupo');
          navigate('/');
          return;
        }

        // 1. Cargar catálogo completo (grupo, productos, customizations) — todo desde backend
        // STOCK CHECKING (data source): each product includes is_available used for stock checks below
        setIsLoadingAvailability(true);
        const catalogResult = await productService.getCatalog(groupCode);
        console.log('catalogResult', catalogResult);

        if (!catalogResult) {
          setCatalogError(true);
          setIsLoadingAvailability(false);
          setIsLoading(false);
          return;
        }

        // 2. Construir groupData desde el catálogo (prendas, colores, tallas, imágenes, opciones)
        let storedGroup: Record<string, unknown> | null = null;
        let storedUserShippingType: unknown = null;
        try {
          const authUserData = localStorage.getItem('userData');
          if (authUserData) {
            const parsed = JSON.parse(authUserData);
            storedGroup = parsed?.group ?? null;
            storedUserShippingType = parsed?.user?.shipping_type ?? null;
          }
        } catch {
          storedGroup = null;
          storedUserShippingType = null;
        }

        const catalogGroup = buildCatalogGroupData(
          catalogResult.group,
          catalogResult.products,
          catalogResult.customizations,
          storedGroup
        );
        // Preselect user's previous shipping choice (hybrid); forced options for grouped/individual
        setDeliveryChoice(resolveDeliveryChoice(catalogGroup.delivery_option, storedUserShippingType));

        // 4. Mapa de disponibilidad desde productos del catálogo
        // STOCK CHECKING (build map): resolveStockStatus(product) → productAvailability[garment_color_size]
        const availabilityMap: {[key: string]: StockStatus} = {};
        catalogResult.products.forEach((product: ProductForGroup) => {
          const key = getAvailabilityKey(product.garment_id, product.color_id, product.size_id);
          availabilityMap[key] = resolveStockStatus(product);
        });

        setProductAvailability(availabilityMap);
        setCatalogProducts(catalogResult.products);
        setIsLoadingAvailability(false);
        setGroupData(catalogGroup);
        
        // Variables para control de carga de datos
        let userDataLoaded = false;
        let productLoaded = false;
        let previousColorId = null;
        
        // PASO 1: Verificar si el usuario está autenticado con credenciales (no solo código de grupo)
        // Si está autenticado con credenciales, cargar sus datos automáticamente
        // Si solo entró con código de grupo, requerir que ingrese sus datos
        
        // PASO 2: Cargar datos del usuario y producto desde 'userData'
        try {
          const authUserData = localStorage.getItem('userData');
          const userType = localStorage.getItem('userType');
          const authToken = localStorage.getItem('authToken');
          
          if (authUserData && userType === 'member') {
            const parsedData = JSON.parse(authUserData);
            
            // Verificar que parsedData tenga la estructura esperada
            if (!parsedData || typeof parsedData !== 'object') {
              throw new Error('Formato de datos de usuario inválido');
            }
            
            const user = parsedData.user;
            const storedGroup = parsedData.group;
            
            // Verificar que sea el mismo grupo
            if (storedGroup && storedGroup.id === catalogResult.group.id) {
              
              // Verificar si el usuario se autenticó con credenciales o solo con código de grupo
              const isAuthenticatedUser = authToken && !authToken.startsWith(btoa('group:'));
              
              // Si el usuario está autenticado con credenciales y tiene datos completos, cargarlos automáticamente
              if (isAuthenticatedUser && user && user.name && user.email && !userDataLoaded) {
                
                // Detectar si es un cambio (el usuario ya tiene un pedido)
                const hasExistingOrder = user.id && user.product_id;
                
                if (hasExistingOrder) {
                  setIsChange(true);
                }
                
                const newOrderData = {
                  name: user.name,
                  email: user.email,
                  phone: user.phone || '',
                  group_id: catalogResult.group.id,
                  product_id: user.product_id,
                  id: user.id,
                  customOptions: user.customizations || [],
                  colorId: user.product?.color_id || null,
                  sizeId: user.product?.size.name || null,
                  garmentId: user.product?.garment_id || null,
                  delivery_address: user.delivery_address || null,
                  shipping_type: user.shipping_type || null,
                };
                
                setOrderData(newOrderData);
                setDeliveryChoice(
                  resolveDeliveryChoice(catalogGroup.delivery_option, user.shipping_type)
                );
                userDataLoaded = true;
              }
              
              if (user && typeof user === 'object') {
                productLoaded = safelyExtractProductData(user, catalogGroup);
                
                const loadUserCustomizations = () => {
                  if (!user.customizations?.length) return;
                  const loadedCustomOptions: {[key: number]: string} = {};
                  catalogGroup.group_customizations.forEach((customization: any) => {
                    loadedCustomOptions[customization.option_id] = '';
                  });
                  user.customizations.forEach((custom: { option_id?: number; value?: string }) => {
                    if (custom.option_id && custom.value !== undefined) {
                      loadedCustomOptions[custom.option_id] = custom.value;
                    }
                  });
                  setCustomOptions(loadedCustomOptions);
                };

                if (productLoaded) {
                  loadUserCustomizations();
                } else if (user.product && user.product_id) {
                  if (user.product.garment && user.product.garment_id) {
                    setSelectedGarment({
                      id: user.product.garment_id,
                      name: user.product.garment?.name || ''
                    });
                    if (user.product.color && user.product.color_id) {
                      setSelectedColor({
                        id: user.product.color_id,
                        name: user.product.color?.name || '',
                        hex_code: user.product.color?.hex_code || '#000000',
                      });
                    }
                    if (user.product.size && user.product.size_id) {
                      setSelectedSize(user.product.size.name || '');
                    }
                    productLoaded = true;
                  }
                  loadUserCustomizations();
                }
              }
            }
          }
        } catch {
          // localStorage data corrupt or missing — will show info modal
        }
        
        // PASO 3: Si aún no hay datos de usuario, mostrar modal para recogerlos
        if (!userDataLoaded) {
          setShowOrderInfoModal(true);
        }
        
        // PASO 4: Si no se cargó un producto, establecer opciones por defecto
        if (!productLoaded) {
          const availableSizes = catalogGroup.group_sizes.map((s: any) => s.size.name);
          
          if (catalogGroup.group_garments.length > 0) {
            const firstGarment = catalogGroup.group_garments[0];
            setSelectedGarment({
              id: firstGarment.garment.id,
              name: firstGarment.garment.name
            });
            
            const availableColors = catalogGroup.group_colors.filter((c: any) => c.garment_id === firstGarment.garment.id);
            
            // Intentar seleccionar el color previamente guardado si está disponible
            if (previousColorId && availableColors.some((c: any) => c.color.id === previousColorId)) {
              const savedColor = availableColors.find((c: any) => c.color.id === previousColorId);
              if (savedColor) {
                setSelectedColor({
                  id: savedColor.color.id,
                  name: savedColor.color.name,
                  hex_code: savedColor.color.hex_code,
                });
              }
            } 
            else if (availableColors.length > 0) {
              setSelectedColor({
                id: availableColors[0].color.id,
                name: availableColors[0].color.name,
                hex_code: availableColors[0].color.hex_code,
              });
            }
            
            // Seleccionar primera talla disponible si no hay una preferida
            if (!selectedSize && availableSizes.length > 0) {
              // Seleccionar la primera talla por ahora, la disponibilidad se verificará después
              setSelectedSize(availableSizes[0]);
            }
          }
        }
        
        // PASO 5: Inicializar opciones de personalización por defecto si no se cargaron antes
        if (!productLoaded) {
          const defaultCustomOptions: {[key: number]: string} = {};
          
          catalogGroup.group_customizations.forEach((customization: any) => {
            if (customization.option.value_type === 'boolean') {
              defaultCustomOptions[customization.option_id] = '';
            } else {
              defaultCustomOptions[customization.option_id] = '';
            }
          });
          
          setCustomOptions(defaultCustomOptions);
        }
        
        setIsLoading(false);

      } catch (error) {
        setCatalogError(true);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [groupCode, navigate]);

  /**
   * Calculates prices using the backend API whenever the product selection changes.
   * Falls back to catalog-derived prices when the full combination is not yet complete.
   * Debounced 300 ms to avoid flooding the API while the user types customizations.
   */
  useEffect(() => {
    if (!groupData) return;

    // Optimistic local prices while waiting for API (uses catalog base prices)
    const garmentPrice = selectedGarment ? (garmentPriceMap[selectedGarment.id] || 0) : 0;
    const colorPrice   = selectedColor   ? (colorPriceMap[selectedColor.id]     || 0) : 0;
    const sizePrice    = (() => {
      if (!selectedSize) return 0;
      const sizeData = groupData.group_sizes.find((s: any) => s.size.name === selectedSize);
      return sizeData ? (sizePriceMap[sizeData.size.id] || 0) : 0;
    })();

    if (!selectedGarment || !selectedColor || !selectedSize) {
      setPriceBreakdown({ garment: garmentPrice, color: colorPrice, size: sizePrice, customizations: 0, delivery_price: 0 });
      setTotalPrice(garmentPrice + colorPrice + sizePrice);
      setPriceError(false);
      setIsCalculatingPrice(false);
      return;
    }

    const sizeData = groupData.group_sizes.find((s: any) => s.size.name === selectedSize);
    if (!sizeData) return;

    // Resolve product_id from catalog
    const catalogProduct = catalogProducts.find(p =>
      p.garment_id === selectedGarment.id &&
      p.color_id   === selectedColor.id &&
      p.size_id    === sizeData.size.id
    );
    if (!catalogProduct) return;

    // Build customizations list for the API
    const customizationsForApi = Object.entries(customOptions)
      .filter(([, value]) => value && value !== 'false' && value.trim() !== '')
      .map(([optionIdStr, value]) => ({ option_id: parseInt(optionIdStr), value }));

    setIsCalculatingPrice(true);

    const timer = setTimeout(async () => {
      const existingId = (() => {
        const stored = localStorage.getItem('userData');
        if (stored) {
          const { user } = JSON.parse(stored);
          return user?.id || null;
        }
        return orderData?.id || null;
      })();

      const result = await productService.calculatePrice(groupData.id, {
        product_id: catalogProduct.product_id,
        customizations: customizationsForApi,
        previous_order_id: isChange && existingId ? existingId : null,
        delivery_choice: deliveryChoice,
      });

      if (!result) {
        setPriceError(true);
        setIsCalculatingPrice(false);
        return;
      }

      setPriceError(false);
      const total = Number(result.total_price) || 0;
      const customizationsPrice = Number(result.breakdown.customizations_price) || 0;
      const deliveryPrice = Number(result.breakdown.delivery_price) || 0;
      setPriceBreakdown({
        garment:        garmentPrice,
        color:          colorPrice,
        size:           sizePrice,
        customizations: customizationsPrice,
        delivery_price: deliveryPrice,
      });
      setTotalPrice(total);
      setOrderChange(result.order_change ? {
        ...result.order_change,
        net_paid_amount: Number(result.order_change.net_paid_amount) || 0,
        new_price: Number(result.order_change.new_price) || 0,
        price_difference: Number(result.order_change.price_difference) || 0,
      } : null);
      setIsCalculatingPrice(false);
    }, 300);

    return () => { clearTimeout(timer); setIsCalculatingPrice(false); };
  }, [groupData, selectedGarment, selectedColor, selectedSize, customOptions,
      garmentPriceMap, colorPriceMap, sizePriceMap, catalogProducts, isChange, orderData,
      deliveryChoice]);

  const deliveryPrice = useMemo(() => {
    if (!groupData) return 0;
    if (deliveryChoice === 'individual') return Number(groupData.individual_option_price) || 0;
    return Number(groupData.grouped_option_price) || 0;
  }, [groupData, deliveryChoice]);

  const adjustedOrderChange = orderChange;
  const displayTotal = totalPrice;

  const handleOrderInfoSubmit = (data: any) => {
    if (!groupData?.id) {
      toast.error('Error al guardar la información');
      return;
    }

    const newOrderData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      group_id: groupData.id,
      product_id: data.product_id,
      id: data.id
    };
    
    setOrderData(newOrderData);
    
    // Guardar los datos en localStorage para futuras sesiones
    // Esto actualiza los datos del usuario en la sesión actual
    try {
      const currentUserData = localStorage.getItem('userData');
      if (currentUserData) {
        const parsedData = JSON.parse(currentUserData);
        if (parsedData.user) {
          // Actualizar los datos del usuario con la nueva información
          parsedData.user = {
            ...parsedData.user,
            name: data.name,
            email: data.email,
            phone: data.phone
          };
          localStorage.setItem('userData', JSON.stringify(parsedData));
        }
      }
    } catch {
      // localStorage write failed — non-critical
    }
    
    toast.success('Información guardada correctamente');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orderData) {
      toast.error('Por favor, completa tu información personal primero');
      setShowOrderInfoModal(true);
      return;
    }

    // Validar que tenemos opciones seleccionadas
    if (!groupData?.id) {
      toast.error('Error al cargar datos del grupo');
      return;
    }

    // Si no hay opciones seleccionadas, intentar seleccionar automáticamente las primeras opciones disponibles
    let currentGarment = selectedGarment;
    let currentColor = selectedColor;
    let currentSize = selectedSize;

    // Si no hay prenda seleccionada, seleccionar la primera disponible
    if (!currentGarment && groupData.group_garments.length > 0) {
      const firstGarment = groupData.group_garments[0];
      currentGarment = {
        id: firstGarment.garment.id,
        name: firstGarment.garment.name
      };
      setSelectedGarment(currentGarment);
    }

    // Si no hay color seleccionado pero hay prenda, buscar el primer color disponible
    if (currentGarment && !currentColor) {
      const availableColors = groupData.group_colors.filter((c: any) => c.garment_id === currentGarment.id);
      if (availableColors.length > 0) {
        currentColor = {
          id: availableColors[0].color.id,
          name: availableColors[0].color.name,
          hex_code: availableColors[0].color.hex_code,
        };
        setSelectedColor(currentColor);
      }
    }

    // Si no hay talla seleccionada, seleccionar la primera disponible
    if (!currentSize && groupData.group_sizes.length > 0) {
      currentSize = groupData.group_sizes[0].size.name;
      setSelectedSize(currentSize);
    }

    // Verificar si sigue faltando alguna opción
    if (!currentGarment || !currentColor || !currentSize) {
      toast.error('Por favor, selecciona todas las opciones');
      return;
    }

    // Block proceed when mandatory text customizations are empty
    const missingMandatory = groupData.group_customizations.filter((c) => {
      if (!c.is_mandatory) return false;
      const value = customOptions[c.option_id];
      return !value || value.trim() === '';
    });
    if (missingMandatory.length > 0) {
      const names = missingMandatory.map((c) => c.option.name).join(', ');
      toast.error(
        missingMandatory.length === 1
          ? `El campo "${names}" es obligatorio`
          : `Los campos siguientes son obligatorios: ${names}`
      );
      return;
    }

    try {
      // Preparar customizaciones
      const customizations: TempCustomization[] = [];

      // Procesar todas las opciones de personalización (incluidas las bloqueadas)
      Object.entries(customOptions).forEach(([optionIdStr, value]) => {
        // Solo procesar valores no vacíos y valores explícitamente booleanos
        const optionId = parseInt(optionIdStr);
        const optionData = groupData.group_customizations.find((c: any) => c.option_id === optionId);
        
        if (optionData) {
          // Para opciones booleanas, solo incluir si el valor es "true"
          if (optionData.option.value_type === 'boolean') {
            if (value === 'true') {
              customizations.push({
                option_id: optionId,
                value: 'true',
                option: {
                  name: optionData.option.name
                },
                price: Number(optionData.price) || 0,
                custom_value: optionData.custom_value ?? undefined,
                is_locked: optionData.is_locked || false
              });
            }
          } 
          // Para otras opciones, solo incluir si tienen un valor no vacío
          else if (value && value.trim() !== '') {
            customizations.push({
              option_id: optionId,
              value: value,
              option: {
                name: optionData.option.name
              },
              price: Number(optionData.price) || 0,
              custom_value: optionData.custom_value ?? undefined,
              is_locked: optionData.is_locked || false
            });
          }
        }
      });

      const sizeData = groupData.group_sizes.find((s: any) => s.size.name === currentSize);
      if (!sizeData) {
        toast.error('Error: Talla no válida');
        return;
      }

      // Resolve product_id from catalog (no extra DB query needed)
      const catalogProduct = catalogProducts.find(p =>
        p.garment_id === currentGarment.id &&
        p.color_id   === currentColor.id &&
        p.size_id    === sizeData.size.id
      );

      if (!catalogProduct) {
        toast.error('Error: No se encontró un producto activo con las características seleccionadas');
        return;
      }

      // Get authoritative price + order_change info from backend
      const customizationsForApi = customizations
        .filter(c => !c.is_locked)
        .map(c => ({ option_id: c.option_id, value: c.value }));

      const existingOrderId = (() => {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const { user } = JSON.parse(storedUserData);
          return user?.id || null;
        }
        return null;
      })();

      const priceResult = await productService.calculatePrice(groupData.id, {
        product_id: catalogProduct.product_id,
        customizations: customizationsForApi,
        previous_order_id: isChange && existingOrderId ? existingOrderId : null,
        delivery_choice: deliveryChoice,
      });

      if (!priceResult) {
        toast.error('Error al calcular el precio. Inténtalo de nuevo.');
        return;
      }

      const newOrder = {
        id: existingOrderId || order?.id,
        group_id: groupData.id,
        garment_id: currentGarment.id,
        garment_name: currentGarment.name,
        garment_price: garmentPriceMap[currentGarment.id] || 0,
        color_id: currentColor.id,
        color_name: currentColor.name,
        color_hex_code: currentColor.hex_code,
        color_price: colorPriceMap[currentColor.id] || 0,
        size_id: sizeData.size.id,
        size_name: currentSize,
        size_price: sizePriceMap[sizeData.size.id] || 0,
        product_id: catalogProduct.product_id,
        customizations: customizations,
        total_price: Number(priceResult.breakdown.subtotal) || 0,
        final_price: Number(priceResult.total_price) || 0,
        status: 'pending',
        name: orderData.name,
        email: orderData.email || '',
        phone: orderData.phone || '',
        is_change: isChange,
        delivery_option: groupData.delivery_option,
        delivery_choice: deliveryChoice,
        delivery_price: Number(priceResult.breakdown.delivery_price) || deliveryPrice,
        group_delivery_address: pickGroupDeliveryAddress(groupData.delivery_addresses),
        order_change: priceResult.order_change ? {
          ...priceResult.order_change,
          net_paid_amount: Number(priceResult.order_change.net_paid_amount) || 0,
          new_price: Number(priceResult.order_change.new_price) || 0,
          price_difference: Number(priceResult.order_change.price_difference) || 0,
        } : null,
      } as OrderState;

      setOrder(newOrder);
      setShowSummary(true);
    } catch (error: any) {
      toast.error('Error al preparar el pedido: ' + error.message);
    }
  };

  /**
   * Handles changes in customization options
   */
  const handleCustomizationChange = (optionId: number, value: string) => {
    // Actualizar el valor de la customización
    setCustomOptions(prev => {
      // Si el valor está vacío o es "false", eliminar la opción del objeto
      if (value === '' || value === 'false') {
        const newOptions = { ...prev };
        delete newOptions[optionId];
        return newOptions;
      }
      
      // Si hay un valor válido, actualizarlo
      return {
        ...prev,
        [optionId]: value
      };
    });
  };

  // Preselect the mandatory options
  useEffect(() => {
    if (groupData) {
      groupData.group_customizations.forEach((c: any) => {
        if (c.is_mandatory && c.option.value_type === 'boolean') {
          handleCustomizationChange(c.option_id, 'true');
        }
      });
    }
  }, [groupData]);

  /**
   * Renders boolean customization options as checkboxes
   */
  const renderBooleanOptions = () => {
    if (!groupData) return null;
    
    const booleanOptions = groupData.group_customizations.filter((c: any) => c.option.value_type === 'boolean');
    
    if (booleanOptions.length === 0) return null;
    
    return (
      <div>
        <h4 className="text-sm font-medium text-gray-600 mb-2">Selecciona las opciones que necesites:</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {booleanOptions.map(({ option, option_id, price: rawPrice, custom_value, is_locked, is_mandatory }) => {
            const price = Number(rawPrice) || 0;
            const wasPreviouslySelected = isChange && orderData?.customOptions?.some(
              (opt: any) => opt.option_id === option_id && opt.value === 'true'
            );
            return (
            <label key={option_id} className={`flex items-center gap-2 py-1 px-2 rounded relative transition-colors ${
              wasPreviouslySelected ? 'bg-blue-50/50' : ''
            }`}>
              {wasPreviouslySelected && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white shadow-sm z-10" />
              )}
              <input
                type="checkbox"
                checked={customOptions[option_id] === 'true'}
                onChange={(e) => handleCustomizationChange(
                  option_id,
                  e.target.checked ? 'true' : 'false'
                )}
                className="h-4 w-4 text-[#F90000] rounded border-gray-300 focus:ring-[#F90000]"
                required={is_mandatory}
                aria-required={is_mandatory}
                disabled={is_mandatory}
              />
              <div className="relative group">
                <span className="text-sm text-gray-700">
                  {option.name}
                  {is_mandatory && (
                    <span className="text-[#F90000] ml-0.5">*</span>
                  )}
                  {custom_value && (
                    <span className="text-gray-500"> ({custom_value})</span>
                  )}
                </span>
                {price > 0 && (
                  <span className="ml-1 text-xs text-[#F90000]">{is_locked ? "" : `+${price.toFixed(2)}€`}</span>
                )}

                {is_mandatory && <div className="absolute bottom-full hidden group-hover:block bg-gray-500 whitespace-nowrap rounded-full border border-white shadow-sm z-10 text-white text-xs px-2 py-1">
                  Esta opción es obligatoria.
                </div>}
              </div>
            </label>
          );
          })}
        </div>
      </div>
    );
  };

  /**
   * Renders text and select customization options
   */
  const renderTextAndSelectOptions = () => {
    if (!groupData) return null;
    
    const otherOptions = groupData.group_customizations.filter((c: any) => c.option.value_type !== 'boolean');
    
    if (otherOptions.length === 0) return null;
    
    return (
      <div>
        <h4 className="text-sm font-medium text-gray-600 mb-2">Personalización adicional:</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {otherOptions.map(({ option, option_id, price: rawPrice, custom_value, is_locked, is_mandatory }) => {
            const price = Number(rawPrice) || 0;
            const previousValue = isChange
              ? orderData?.customOptions?.find((opt: any) => opt.option_id === option_id)?.value
              : undefined;
            const hasPreviousValue = previousValue !== undefined && previousValue !== '';
            return (
            <div key={option_id} className={`mb-1 relative ${hasPreviousValue ? 'bg-blue-50/50 rounded-lg p-2' : ''}`}>
              {hasPreviousValue && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white shadow-sm z-10" />
              )}
              <label className="block text-sm font-medium text-gray-700 mb-1 relative group w-fit">
                {option.name}
                {is_mandatory && (
                  <span className="text-[#F90000] ml-0.5">*</span>
                )}
                {custom_value && (
                  <span className="text-gray-500"> ({custom_value})</span>
                )}
                {price > 0 && (
                  <span className="text-[#F90000] text-xs ml-1">{is_locked ? "" : `+${price.toFixed(2)}€`}</span>
                )}
                {is_mandatory && <div className="absolute bottom-full hidden group-hover:block bg-gray-500 whitespace-nowrap rounded-full border border-white shadow-sm z-10 text-white text-xs px-2 py-1">
                  Esta opción es obligatoria.
                </div>}
              </label>
              {option.value_type === 'text' ? (
                <input
                  type="text"
                  value={customOptions[option_id] || ''}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (option.name === 'Iniciales') value = value.toUpperCase();
                    handleCustomizationChange(option_id, value);
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-[#F90000] focus:border-[#F90000]"
                  maxLength={option.name === 'Iniciales' ? 4 : undefined}
                  placeholder={hasPreviousValue ? `Actual: ${previousValue}` : `Introduce ${option.name.toLowerCase()}`}
                  required={is_mandatory}
                  aria-required={is_mandatory}
                />
              ) : (
                <select
                  value={customOptions[option_id] || ''}
                  onChange={(e) => handleCustomizationChange(option_id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-[#F90000] focus:border-[#F90000]"
                  title={`Seleccionar ${option.name.toLowerCase()}`}
                >
                  <option value="">Seleccionar</option>
                  {option.possible_values?.map((value: any) => (
                    <option key={value.id} value={value.id}>
                      {value.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
          })}
        </div>
      </div>
    );
  };

  /**
   * Checks if the current selection (garment + color + size) is available for purchase
   * STOCK CHECKING (purchase gate): used by the buy button to block out-of-stock selections
   */
  const isCurrentSelectionAvailable = useCallback((): boolean => {
    if (isLoadingAvailability) return true;
    if (!selectedGarment || !selectedColor || !selectedSize) return false;
    
    const sizeData = groupData?.group_sizes.find((s: any) => s.size.name === selectedSize);
    if (!sizeData) return false;
    
    return isProductAvailableInStock(selectedGarment.id, selectedColor.id, sizeData.size.id);
  }, [selectedGarment, selectedColor, selectedSize, groupData, isProductAvailableInStock, isLoadingAvailability]);

  /**
   * STOCK CHECKING: current selection relies on incoming reserved stock (show delay message)
   */
  const isCurrentSelectionPendingIncoming = useCallback((): boolean => {
    if (isLoadingAvailability) return false;
    if (!selectedGarment || !selectedColor || !selectedSize) return false;

    const sizeData = groupData?.group_sizes.find((s: any) => s.size.name === selectedSize);
    if (!sizeData) return false;

    return isPendingIncomingStock(selectedGarment.id, selectedColor.id, sizeData.size.id);
  }, [selectedGarment, selectedColor, selectedSize, groupData, isPendingIncomingStock, isLoadingAvailability]);

  if (isLoading) {
    return <Loader />;
  }

  if (catalogError || !groupData) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <Header />
        <div className="flex-grow flex items-center justify-center px-4 py-16">
          <div className="max-w-md w-full text-center">
            <div className="bg-white/95 backdrop-blur-sm border-2 border-[#FF0000] p-10 space-y-6">
              <img
                src="https://fraeternity.myshopify.com/cdn/shop/files/LOGO_FTTY_1.png?v=1737489768&width=570"
                alt="FRAETERNITY"
                className="h-8 mx-auto opacity-30"
              />
              <div className="w-12 h-0.5 bg-[#FF0000] mx-auto" />
              <div className="space-y-2">
                <h2
                  className="text-xl tracking-tight text-gray-900"
                  style={{ fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif", fontWeight: 700, fontStyle: 'oblique' }}
                >
                  ALGO HA SALIDO MAL
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                  No hemos podido cargar los productos. Comprueba tu conexión a internet y vuelve a intentarlo.
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={() => {
                    setCatalogError(false);
                    setIsLoading(true);
                    window.location.reload();
                  }}
                  className="w-full py-3 bg-[#FF0000] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#D80000] transition-colors"
                  style={{ fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif", fontStyle: 'oblique' }}
                >
                  Reintentar
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 border border-gray-300 text-sm font-bold uppercase tracking-wider text-gray-600 hover:border-[#FF0000] hover:text-[#FF0000] transition-colors"
                  style={{ fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif", fontStyle: 'oblique' }}
                >
                  Volver al inicio
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundImage: 'url(/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'}}>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="flex items-center justify-between gap-2 sm:gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#F90000]">{groupData.name}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Columna izquierda - Imágenes */}
          <div className="flex flex-col h-full">
            {selectedGarment && selectedColor && (
              <>
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden shadow-sm flex-grow relative">
                  {(() => {
                    // Buscar datos del color usando el garment_id y color_id correcto
                    const colorData = groupData.group_colors.find((c: any) => 
                      c.color_id === selectedColor?.id && 
                      c.garment_id === selectedGarment?.id
                    );
                    const images = colorData?.images;
                    
                    // Verificar si tenemos imágenes disponibles
                    const hasImages = images && (
                      images.front_image_url || 
                      images.back_image_url || 
                      images.side_image_url || 
                      images.detail_image_url
                    );
                    
                    if (hasImages) {
                      const availableImages = [
                        { key: 'front', url: images.front_image_url, label: 'Frontal' },
                        { key: 'back', url: images.back_image_url, label: 'Trasera' },
                        { key: 'side', url: images.side_image_url, label: 'Lateral' },
                        { key: 'detail', url: images.detail_image_url, label: 'Detalle' }
                      ].filter(img => img.url);
                      
                      if (availableImages.length > 1) {
                        // Funciones para navegar entre imágenes
                        const currentIndex = availableImages.findIndex(img => img.key === currentImageView);
                        
                        const goToPrevious = () => {
                          if (isZoomed) return; // No navegar si está en zoom
                          const prevIndex = (currentIndex - 1 + availableImages.length) % availableImages.length;
                          setCurrentImageView(availableImages[prevIndex].key as any);
                        };
                        
                        const goToNext = () => {
                          if (isZoomed) return; // No navegar si está en zoom
                          const nextIndex = (currentIndex + 1) % availableImages.length;
                          setCurrentImageView(availableImages[nextIndex].key as any);
                        };
                        
                        return (
                          <>
                          {/* Minimapa (oculto durante zoom) */}
                          {!isZoomed && (
                            <div className="absolute top-2 left-2 flex gap-1 sm:gap-1.5 z-10 bg-white/70 backdrop-blur-sm p-1 sm:p-1.5 rounded-md shadow-sm transition-opacity duration-300 hover:bg-white/90 minimap-container">
                              {availableImages.map(img => (
                                <div 
                                  key={img.key} 
                                  className={`w-10 h-10 sm:w-12 sm:h-12 bg-white rounded overflow-hidden cursor-pointer transition-all ${
                                    currentImageView === img.key ? 
                                      'ring-2 ring-[#F90000] shadow-md scale-110' : 
                                                                              'ring-1 ring-gray-200 hover:ring-[#F90000] opacity-85 hover:opacity-100 hover:scale-105'
                                  }`}
                                  title={img.label}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Evitar activar el zoom al hacer clic en miniaturas
                                    setCurrentImageView(img.key as any);
                                  }}
                                >
                                  <div className="relative w-full h-full">
                                    <img 
                                      src={img.url || ''} 
                                      alt={img.label}
                                      className="w-full h-full object-cover image-viewer"
                                      draggable="false"
                                    />
                                    {currentImageView === img.key && (
                                      <div className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 bg-[#F90000]"></div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Controles de navegación (ocultos durante zoom) */}
                          {!isZoomed && (
                            <div className="absolute inset-y-0 left-0 right-0 flex justify-between items-center px-1 sm:px-2 transition-opacity duration-300 z-20 pointer-events-none navigation-buttons">
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); // Evitar activar el zoom al hacer clic
                                  goToPrevious(); 
                                }}
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white flex items-center justify-center text-gray-800 hover:text-[#F90000] transition-all shadow-md opacity-90 hover:opacity-100 transform hover:scale-105 active:scale-95 pointer-events-auto"
                                aria-label="Vista anterior"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </button>
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); // Evitar activar el zoom al hacer clic
                                  goToNext(); 
                                }}
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white flex items-center justify-center text-gray-800 hover:text-[#F90000] transition-all shadow-md opacity-90 hover:opacity-100 transform hover:scale-105 active:scale-95 pointer-events-auto"
                                aria-label="Vista siguiente"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          )}
                          
                          {/* Indicador de navegación (oculto durante zoom) */}
                          {!isZoomed && availableImages.length > 1 && (
                            <div className="absolute bottom-2 right-2 z-30 transition-opacity duration-300">
                              <div className="bg-white/80 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium text-gray-800 shadow-sm flex items-center gap-1 sm:gap-2">
                                <span className="text-[#F90000] hidden sm:inline">{
                                  availableImages.find(img => img.key === currentImageView)?.label || 'Vista'
                                }</span>
                                <span className="text-gray-500 text-xs">
                                  {availableImages.findIndex(img => img.key === currentImageView) + 1}/{availableImages.length}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {/* Instrucción de zoom */}
                          {!isZoomed && (
                            <div className="absolute bottom-2 left-2 z-30 opacity-70 hover:opacity-100 transition-opacity duration-300">
                              <div className="bg-white/80 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium text-gray-800 shadow-sm flex items-center gap-1 sm:gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M5 8a1 1 0 011-1h1V6a1 1 0 012 0v1h1a1 1 0 110 2H9v1a1 1 0 11-2 0V9H6a1 1 0 01-1-1z" />
                                  <path fillRule="evenodd" d="M2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8zm6-4a4 4 0 100 8 4 4 0 000-8z" clipRule="evenodd" />
                                </svg>
                                <span className="hidden sm:inline">Ampliar imagen</span>
                                <span className="inline sm:hidden">Ampliar</span>
                              </div>
                            </div>
                          )}
                          </>
                        );
                      }
                    }
                    return null;
                  })()}

                  {(() => {
                    // Buscar datos del color usando el garment_id y color_id correcto
                    const colorData = groupData.group_colors.find((c: any) => 
                      c.color_id === selectedColor?.id && 
                      c.garment_id === selectedGarment?.id
                    );
                    const images = colorData?.images;
                    
                    // Verificar si la imagen seleccionada existe
                    
                    // Determinar qué imagen mostrar según la vista actual
                    let imageToShow = null;
                    
                    if (images) {
                      switch (currentImageView) {
                        case 'front':
                          imageToShow = images.front_image_url;
                          break;
                        case 'back':
                          imageToShow = images.back_image_url;
                          break;
                        case 'side':
                          imageToShow = images.side_image_url;
                          break;
                        case 'detail':
                          imageToShow = images.detail_image_url;
                          break;
                      }
                    }
                    
                    // Si no hay imagen para la vista actual, buscar una alternativa
                    let finalImage = imageToShow;
                    if (!finalImage && images) {
                      // Intentar con otras imágenes disponibles
                      finalImage = images.front_image_url || images.back_image_url || images.side_image_url || images.detail_image_url;
                    }
                    
                    // Si aún no hay imagen, usar una imagen de respaldo
                    if (!finalImage && selectedGarment && selectedColor) {
                      finalImage = `/products/${selectedGarment.name}/${selectedColor.name}.png`;
                    }
                    
                    // Si todo falla, usar una imagen genérica
                    if (!finalImage) {
                      finalImage = '/no-image.png';
                    }
                    
                    // Funciones para manejar el zoom
                    const handleZoom = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
                      const container = e.currentTarget;
                      const img = container.querySelector('img');
                      if (!img) return;
                      
                      // Calcular la posición relativa del cursor o toque en el contenedor
                      const rect = container.getBoundingClientRect();
                      
                      // Obtener coordenadas del evento (mouse o touch)
                      let clientX, clientY;
                      
                      if ('touches' in e) {
                        // Es un evento táctil
                        clientX = e.touches[0].clientX;
                        clientY = e.touches[0].clientY;
                      } else {
                        // Es un evento de mouse
                        clientX = e.clientX;
                        clientY = e.clientY;
                      }
                      
                      const x = ((clientX - rect.left) / rect.width) * 100;
                      const y = ((clientY - rect.top) / rect.height) * 100;
                      
                      // Guardar la posición para el zoom
                      setZoomPosition({ x, y });
                      
                      // Activar zoom
                      if (!isZoomed) {
                        setIsZoomed(true);
                      }
                    };
                    
                    // Función para actualizar la posición del zoom
                    const handleMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
                      if (!isZoomed) return;
                      
                      const container = e.currentTarget;
                      const rect = container.getBoundingClientRect();
                      
                      // Obtener coordenadas del evento (mouse o touch)
                      let clientX, clientY;
                      
                      if ('touches' in e) {
                        // Es un evento táctil
                        clientX = e.touches[0].clientX;
                        clientY = e.touches[0].clientY;
                        
                        // Prevenir el desplazamiento de la página en dispositivos táctiles
                        e.preventDefault();
                      } else {
                        // Es un evento de mouse
                        clientX = e.clientX;
                        clientY = e.clientY;
                      }
                      
                      // Calcular nuevas coordenadas relativas
                      let newX = ((clientX - rect.left) / rect.width) * 100;
                      let newY = ((clientY - rect.top) / rect.height) * 100;
                      
                      // Limitar coordenadas para evitar bordes vacíos
                      // Ajustamos los límites según el nivel de zoom
                      const limiter = 50 / zoomLevel;
                      newX = Math.min(Math.max(newX, limiter), 100 - limiter);
                      newY = Math.min(Math.max(newY, limiter), 100 - limiter);
                      
                      // Actualizar la posición del zoom
                      setZoomPosition({ x: newX, y: newY });
                    };
                    
                    // Función para quitar el zoom
                    const handleUnzoom = () => {
                      setIsZoomed(false);
                    };
                    
                    // Manejar clics/toques para alternar el zoom
                    const handleInteraction = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
                      if (isZoomed) {
                        handleUnzoom();
                      } else {
                        handleZoom(e);
                      }
                    };
                    
                    // Función para cambiar el nivel de zoom
                    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
                      if (!isZoomed) return;
                      
                      e.preventDefault();
                      if (e.deltaY < 0) {
                        // Scroll hacia arriba, aumentar zoom
                        setZoomLevel(prev => prev < 2.5 ? (prev + 0.5) as 1.5 | 2 | 2.5 : prev);
                      } else {
                        // Scroll hacia abajo, disminuir zoom
                        setZoomLevel(prev => prev > 1.5 ? (prev - 0.5) as 1.5 | 2 | 2.5 : prev);
                      }
                    };
                    
                    // Manejar gestos de pellizco para zoom en dispositivos táctiles
                    const handlePinchZoom = (e: React.TouchEvent<HTMLDivElement>) => {
                      if (e.touches.length !== 2) return;
                      
                      // Prevenir comportamiento predeterminado
                      e.preventDefault();
                      
                      // Calcular la distancia entre los dos toques
                      const touch1 = e.touches[0];
                      const touch2 = e.touches[1];
                      const distance = Math.sqrt(
                        Math.pow(touch2.clientX - touch1.clientX, 2) + 
                        Math.pow(touch2.clientY - touch1.clientY, 2)
                      );
                      
                      // Guardar la distancia inicial
                      if (!e.currentTarget.dataset.pinchDistance) {
                        e.currentTarget.dataset.pinchDistance = distance.toString();
                        return;
                      }
                      
                      // Calcular la diferencia con la distancia anterior
                      const prevDistance = parseFloat(e.currentTarget.dataset.pinchDistance);
                      const delta = distance - prevDistance;
                      
                      // Ajustar el nivel de zoom según el gesto
                      if (Math.abs(delta) > 10) { // Umbral para evitar cambios pequeños
                        if (delta > 0) {
                          // Separar dedos = aumentar zoom
                          setZoomLevel(prev => prev < 2.5 ? (prev + 0.5) as 1.5 | 2 | 2.5 : prev);
                        } else {
                          // Juntar dedos = reducir zoom
                          setZoomLevel(prev => prev > 1.5 ? (prev - 0.5) as 1.5 | 2 | 2.5 : prev);
                        }
                        
                        // Actualizar la distancia guardada
                        e.currentTarget.dataset.pinchDistance = distance.toString();
                      }
                    };
                    
                    // Limpiar datos de pellizco cuando termina el toque
                    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
                      if (e.currentTarget.dataset.pinchDistance) {
                        delete e.currentTarget.dataset.pinchDistance;
                      }
                    };
                    
                    // Determinar la clase de zoom basada en el nivel
                    const getZoomClass = () => {
                      if (!isZoomed) return 'scale-100 zoom-transition';
                      switch (zoomLevel) {
                        case 1.5: return 'scale-150 zoom-transition';
                        case 2: return 'scale-200 zoom-transition';
                        case 2.5: return 'scale-250 zoom-transition';
                        default: return 'scale-200 zoom-transition';
                      }
                    };
                    
                    return (
                      <div 
                        className={`w-full h-full relative image-viewer ${isZoomed ? 'zoomed' : ''}`}
                      >
                        <div 
                          className="absolute inset-0 flex items-center justify-center overflow-hidden"
                          onClick={(e) => {
                            // Solo activar el zoom si se hace clic directamente en este div o en la imagen
                            if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'IMG') {
                              handleInteraction(e);
                            }
                          }}
                          onTouchStart={(e) => {
                            // Para eventos táctiles
                            if (e.touches.length === 1 && (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'IMG')) {
                              handleInteraction(e);
                            } else if (e.touches.length === 2) {
                              handlePinchZoom(e);
                            }
                          }}
                          onTouchMove={(e) => {
                            if (isZoomed) {
                              if (e.touches.length === 1) {
                                handleMove(e);
                              } else if (e.touches.length === 2) {
                                handlePinchZoom(e);
                              }
                            }
                          }}
                          onTouchEnd={handleTouchEnd}
                          onMouseMove={(e) => isZoomed && handleMove(e)}
                          onWheel={isZoomed ? handleWheel : undefined}
                        >
                          <img 
                            key={currentImageView}
                            src={finalImage}
                            alt={`${selectedGarment.name} en color ${selectedColor.name}`}
                            className={`w-full h-full object-contain ${isZoomed ? 'cursor-move' : 'cursor-zoom-in'} animate-fade-in ${getZoomClass()}`}
                            style={isZoomed ? { 
                              transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                            } : undefined}
                            draggable="false"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/no-image.png';
                        }}
                      />
                        </div>
                        
                        {/* Controles de zoom (adaptados para móviles) */}
                        {isZoomed && (
                          <div className="absolute inset-x-0 top-0 flex justify-center items-center z-20 p-1 sm:p-2 gap-1 sm:gap-2">
                            {/* Botones de ajuste de zoom */}
                            <div className="flex gap-0.5 sm:gap-1 bg-white/80 backdrop-blur-sm rounded-md shadow-sm">
                              <button 
                                onClick={() => setZoomLevel(prev => prev > 1.5 ? (prev - 0.5) as 1.5 | 2 | 2.5 : prev)}
                                className="p-1.5 sm:p-2 text-gray-800 hover:text-[#F90000] transition-colors"
                                aria-label="Reducir zoom"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                              <div className="py-1.5 px-1 sm:py-2 sm:px-1 text-[10px] sm:text-xs font-medium text-gray-800">
                                {zoomLevel}x
                              </div>
                              <button 
                                onClick={() => setZoomLevel(prev => prev < 2.5 ? (prev + 0.5) as 1.5 | 2 | 2.5 : prev)}
                                className="p-1.5 sm:p-2 text-gray-800 hover:text-[#F90000] transition-colors"
                                aria-label="Aumentar zoom"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                </div>

                            {/* Botón para salir */}
                            <button
                              onClick={handleUnzoom}
                              className="p-1.5 sm:p-2 bg-white/80 backdrop-blur-sm rounded-md text-gray-800 hover:text-[#F90000] shadow-sm transition-colors"
                              aria-label="Salir del zoom"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        )}

                      </div>
                    );
                })()}
                </div>
              </>
            )}
          </div>

          {/* Columna derecha - Detalles del producto */}
          <div className="flex flex-col h-full">
            <div className="flex-grow space-y-4">
              {/* Banner informativo de cambio de pedido */}
              {isChange && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 border border-gray-200">
                  <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span className="text-xs text-gray-500">Modificando pedido — tu selección anterior marcada con <span className="inline-block w-2 h-2 bg-blue-500 rounded-full align-middle mx-0.5" /></span>
                </div>
              )}

              {/* Panel de selección principal */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                {/* Producto */}
                <div className="mb-3">
                  <h3 className="text-xs font-medium text-gray-500 mb-2">PRODUCTO</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {groupData.group_garments.map(({ garment }) => {
                      const garmentPrice = Number(garmentPriceMap[garment.id]) || 0;
                      return (
                        <button
                          key={garment.id}
                          onClick={() => {
                            const newGarment = {
                              id: garment.id,
                              name: garment.name
                            };
                            setSelectedGarment(newGarment);
                            
                            if (selectedColor) {
                              const sameColorInNewGarment = groupData.group_colors.find((c: any) => 
                                c.garment_id === garment.id && 
                                c.color_id === selectedColor.id
                              );
                              
                              if (sameColorInNewGarment) {
                                setSelectedColor({
                                  id: sameColorInNewGarment.color.id,
                                  name: sameColorInNewGarment.color.name,
                                  hex_code: sameColorInNewGarment.color.hex_code,
                                });
                                return;
                              }
                            }
                            
                            selectFirstColor(garment.id, groupData);
                          }}
                          className={`px-3 py-1.5 rounded text-sm transition-all relative ${
                            selectedGarment?.id === garment.id
                              ? 'bg-[#F90000] text-white shadow-sm'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                          title={isChange && orderData?.garmentId === garment.id ? `${garment.name} (tu pedido actual)` : garment.name}
                        >
                          {isChange && orderData?.garmentId === garment.id && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white shadow-sm z-10" />
                          )}
                          <span>{garment.name}</span>
                          {garmentPrice > 0 && <span className="ml-1 text-xs">+{garmentPrice.toFixed(2)}€</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Talla */}
                {/* STOCK CHECKING (UI): isSizeAvailable disables out-of-stock sizes */}
                <div className="mb-3">
                  <h3 className="text-xs font-medium text-gray-500 mb-2">TALLA</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {groupData.group_sizes
                      .sort((a: any, b: any) => a.size.id - b.size.id)
                      .map(({ size }) => {
                        // STOCK CHECKING: stock check for this size with current garment + color
                        const isAvailable = isSizeAvailable(size.id);
                        const isLoading = isLoadingAvailability;
                        const sizePrice = Number(sizePriceMap[size.id]) || 0;
                        
                        return (
                          <button
                            key={size.id}
                            onClick={() => {
                              if (isAvailable || isLoading) {
                                setSelectedSize(size.name);
                              }
                            }}
                            disabled={!isAvailable && !isLoading && selectedGarment !== null && selectedColor !== null}
                            className={`group relative w-12 h-12 rounded text-sm transition-all flex flex-col items-center justify-center ${
                              selectedSize === size.name
                                ? 'bg-[#F90000] text-white shadow-sm'
                                : !isAvailable && !isLoading
                                  ? 'bg-gray-50 text-gray-700 cursor-not-allowed opacity-80'
                                  : isChange && orderData?.sizeId === size.name
                                    ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                            title={isLoading
                              ? `${size.name}`
                              : isAvailable 
                                ? `${size.name}${sizePrice > 0 ? ` (+${sizePrice.toFixed(2)}€)` : ''}${isChange && orderData?.sizeId === size.name ? ' (actual)' : ''}` 
                                : `${size.name} - Fuera de stock`
                            }
                          >
                            {isChange && orderData?.sizeId === size.name && (
                              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white shadow-sm z-10" />
                            )}
                            <span className="font-medium text-xs">{size.name}</span>
                            {sizePrice > 0 && <span className="text-[10px] leading-none">+{sizePrice.toFixed(2)}€</span>}
                            
                            {!isAvailable && !isLoading && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-full h-0.5 bg-amber-500 rotate-45 transform origin-center"></div>
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* Color */}
                {/* STOCK CHECKING (UI): isColorAvailable disables out-of-stock colors */}
                <div>
                  <h3 className="text-xs font-medium text-gray-500 mb-2">COLOR</h3>
                  <div className="flex flex-wrap gap-2">
                    {groupData.group_colors
                      .filter((c: any) => selectedGarment && c.garment_id === selectedGarment.id)
                      .map(({ color }) => {
                        // STOCK CHECKING: stock check for this color with current garment (+ size if selected)
                        const isAvailable = isColorAvailable(color.id);
                        const isLoading = isLoadingAvailability;
                        const colorPrice = Number(colorPriceMap[color.id]) || 0;
                        
                        return (
                          <button
                            key={color.id}
                            onClick={() => {
                              if (isAvailable || isLoading) {
                                setSelectedColor({
                                  id: color.id,
                                  name: color.name,
                                  hex_code: color.hex_code,
                                });
                              }
                            }}
                            disabled={!isAvailable && !isLoading}
                            className={`group relative ${(!isAvailable && !isLoading) ? 'cursor-not-allowed opacity-80' : ''}`}
                            title={isLoading 
                              ? `${color.name}`
                              : isAvailable 
                                ? color.name 
                                : `${color.name} - Fuera de stock`
                            }
                          >
                            {isChange && orderData?.colorId === color.id && (
                              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white shadow-sm z-10" />
                            )}
                            <div
                              style={{ backgroundColor: color.hex_code }}
                              className={`w-7 h-7 rounded transition-all ${
                                selectedColor?.id === color.id 
                                  ? 'ring-2 ring-[#F90000]'
                                  : isAvailable || isLoading
                                    ? 'ring-1 ring-gray-300 hover:ring-2 ring-offset-1 hover:ring-gray-500'
                                    : 'ring-1 ring-gray-500 ring-inset'
                              }`}
                            />
                            {!isAvailable && !isLoading && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-full h-0.5 bg-amber-500 rotate-45 transform origin-center"></div>
                              </div>
                            )}
                            <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                              <span className="text-xs bg-gray-900 text-white px-1.5 py-0.5 rounded">
                                {color.name}
                                {isChange && orderData?.colorId === color.id && ' (actual)'}
                                {colorPrice > 0 && ` +${colorPrice.toFixed(2)}€`}
                                {!isAvailable && !isLoading && ' - Fuera de stock'}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* STOCK CHECKING: only when current selection is pending_incoming */}
                {isCurrentSelectionPendingIncoming() && (
                  <button
                    type="button"
                    onClick={() => setShowPendingStockInfo(true)}
                    className="mt-3 w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center justify-between text-left hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-amber-800 font-bold">
                      Esta talla tiene un plazo de producción ampliado
                    </span>
                    <InfoIcon className="w-4 h-4 text-amber-800 flex-shrink-0 ml-2" />
                  </button>
                )}
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-xs font-medium text-gray-500 mb-4">PERSONALIZACIÓN</h3>
                <div className="space-y-6">
                  {renderBooleanOptions()}
                  {renderTextAndSelectOptions()}
                  
                  {/* Resumen del precio */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {/* Error al calcular precio */}
                    {priceError && selectedGarment && selectedColor && selectedSize && (
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-xs text-red-700">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <span>
                          No se pudo obtener el precio actualizado. Comprueba tu conexión e inténtalo de nuevo.
                        </span>
                      </div>
                    )}

                    {/* Mostrar información de cambio si aplica */}
                    {isChange && adjustedOrderChange && adjustedOrderChange.price_difference !== 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">Cambio de pedido</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Pedido anterior:</span>
                            <span className="font-medium">{adjustedOrderChange.net_paid_amount.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Nuevo pedido:</span>
                            <span className="font-medium">{adjustedOrderChange.new_price.toFixed(2)}€</span>
                          </div>
                          {adjustedOrderChange.requires_payment && adjustedOrderChange.price_difference !== 0 && (
                            <div className="flex justify-between border-t border-blue-200 pt-1 mt-2">
                              <span className="font-medium text-blue-800">Importe a pagar:</span>
                              <span className="font-bold text-green-600">
                                +{Math.abs(adjustedOrderChange.price_difference).toFixed(2)}€
                              </span>
                            </div>
                          )}
                          {adjustedOrderChange.requires_refund && (
                            <div className="flex justify-between border-t border-blue-200 pt-1 mt-2">
                              <span className="font-medium text-blue-800">A devolver:</span>
                              <span className="font-bold text-green-600">
                                {Math.abs(adjustedOrderChange.price_difference).toFixed(2)}€
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {!isChange && !priceError && (
                      <div className={`transition-opacity duration-200 ${isCalculatingPrice ? 'opacity-40' : 'opacity-100'}`}>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Prenda base:</span>
                          <span className="font-medium">{(priceBreakdown.garment).toFixed(2)}€</span>
                        </div>
                        {priceBreakdown.color > 0 && (
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Color:</span>
                            <span className="font-medium">+{priceBreakdown.color.toFixed(2)}€</span>
                          </div>
                        )}
                        {priceBreakdown.size > 0 && (
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Talla:</span>
                            <span className="font-medium">+{priceBreakdown.size.toFixed(2)}€</span>
                          </div>
                        )}
                        {priceBreakdown.customizations > 0 && (
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Personalizaciones:</span>
                            <span className="font-medium">+{priceBreakdown.customizations.toFixed(2)}€</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {!priceError && adjustedOrderChange && adjustedOrderChange.requires_payment && (
                      <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                        <span className="font-medium text-gray-700">TOTAL:</span>
                        <span className={`font-bold transition-opacity duration-200 ${isCalculatingPrice ? 'opacity-40' : 'opacity-100'} text-[#F90000]`}>
                          {isChange && adjustedOrderChange
                            ? adjustedOrderChange.requires_payment
                              ? Math.abs(adjustedOrderChange.price_difference).toFixed(2) + '€'
                              : '0.00€'
                            : displayTotal.toFixed(2) + '€'
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Botón de compra */}
            <button
              onClick={handleSubmit}
              disabled={!isCurrentSelectionAvailable() || !isOrderChanged || priceError || isCalculatingPrice}
              className={`w-full py-4 rounded-lg font-medium shadow-sm hover:shadow-md flex items-center justify-center text-base mt-6 ${
                isCurrentSelectionAvailable() && !priceError
                  ? 'bg-[#F90000] text-white hover:bg-[#E00000] transition-colors'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {/* STOCK CHECKING (buy button): isCurrentSelectionAvailable() blocks purchase when out of stock */}
              {priceError ? (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <span>No disponible — error de conexión</span>
                </div>
              ) : isCurrentSelectionAvailable() ? (
                <>
                  <span className="mr-3">{isChange ? 'Realizar cambios' : 'Comprar ahora'}</span>
                  <span className={`font-bold transition-opacity duration-200 ${isCalculatingPrice ? 'opacity-40' : 'opacity-100'}`}>
                    {isChange && adjustedOrderChange
                      ? adjustedOrderChange.requires_payment
                        ? Math.abs(adjustedOrderChange.price_difference).toFixed(2) + '€'
                        : ''
                      : (displayTotal - priceBreakdown.delivery_price).toFixed(2) + '€'
                    }
                  </span>
                </>
              ) : (
                <>
                  {!selectedGarment || !selectedColor || !selectedSize ? (
                    <span>Seleccione todas las opciones para continuar</span>
                  ) : (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span>Producto fuera de stock</span>
                    </div>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {showOrderInfoModal && (
        <OrderInfoModal
          isOpen={true}
          onSubmit={(data) => {
            handleOrderInfoSubmit(data);
            setShowOrderInfoModal(false);
          }}
        />
      )}

      {showSummary && order && (
        <OrderSummaryModal
          isOpen={true}
          onClose={() => setShowSummary(false)}
          order={order}
          groupId={groupData.id}
          deliveryOption={groupData.delivery_option}
          deliveryChoice={deliveryChoice}
          onDeliveryChoiceChange={setDeliveryChoice}
          deliveryPrice={deliveryPrice}
          individualOptionPrice={Number(groupData.individual_option_price) || 0}
          groupedOptionPrice={Number(groupData.grouped_option_price) || 0}
          groupDeliveryAddress={pickGroupDeliveryAddress(groupData.delivery_addresses)}
          userDeliveryAddress={orderData?.delivery_address}
        />
      )}

      <PendingStockInfoModal
        isOpen={showPendingStockInfo}
        onClose={() => setShowPendingStockInfo(false)}
      />

      <Footer />
    </div>
  );
}